<template>
	<div class="post">
		<div class="post__header flex">
			<span class="date"
				>Dodano: {{ post.date }} przez {{ post.author }}</span
			>
			<div class="post__header__admin">
				<span class="private-icon" v-if="!post.public">
					<Private></Private>
				</span>
				<span class="edit-icon" v-if="admin" @click="edit = !edit">
					<Edit></Edit>
				</span>
				<span
					class="delete-icon"
					v-if="admin"
					@click="deletePostMessage"
				>
					<Delete></Delete>
				</span>
			</div>
		</div>
		<div class="post__body">
			<figure>
				<div class="post__body__image">
					<template v-if="post.media === 'video'">
						<LazyVideo
							v-for="(path, index) in post.path[0]"
							:key="'video-' + index"
							:path="post.path[0][index]"
							:number="index + 1"
							:postNumber="number"
						></LazyVideo>
					</template>
					<template v-else>
						<LazyImage
							v-for="(path, index) in post.path[1]"
							:key="'image-' + index"
							:path="post.path[1][index]"
							:number="index + 1"
							:postNumber="number"
						></LazyImage>
					</template>
				</div>
				<transition name="fade-button" mode="out-in">
					<figcaption v-if="!edit">
						{{ post.title }}
					</figcaption>
					<input
						type="text"
						:value="post.title"
						v-if="admin && edit"
						ref="newTitle"
					/>
				</transition>
			</figure>
		</div>
		<div class="post__footer flex">
			<transition name="fade-button" mode="out-in">
				<ul class="post__footer__tags flex" v-if="!edit">
					<li
						v-for="(tag, index) in post.tags"
						:key="index"
						@click="loadTaggedPosts(tag)"
					>
						{{ tag }}
					</li>
				</ul>
				<textarea
					v-if="admin && edit"
					:value="editableTags"
					ref="newTags"
				></textarea>
			</transition>
			<transition name="fade-button" mode="out-in">
				<div class="edit__private" v-if="admin && edit">
					<input type="checkbox" v-model="post.public" />
					<label>Publiczny</label>
				</div>
			</transition>
			<transition name="fade-button" mode="out-in">
				<button class="btn" v-if="edit" @click="saveUpdatedPost">
					Zapisz
				</button>
			</transition>
			<transition name="fade-button" mode="out-in">
				<button
					class="btn btn-reverse"
					v-if="edit"
					@click="edit = !edit"
				>
					Anuluj
				</button>
			</transition>
			<transition name="fade-button" mode="out-in">
				<div class="post__footer__download flex" v-show="!edit">
					<a
						v-for="(link, index) in post.path[0]"
						:key="index"
						href="#"
						@click.prevent="
							downloadMedia(post.path[0][index], index)
						"
					>
						<Save></Save>
					</a>
				</div>
			</transition>
		</div>
		<div class="post__message" v-if="postMessage">
			<div class="post__message__text">Czy chcesz usunąć post?</div>
			<div class="post__message__buttons">
				<button class="btn" @click="deletePost">Tak</button>
				<button class="btn btn-reverse" @click="hidePostMessage">
					Nie
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";

import Save from "@/assets/img/download-solid.svg";
import Delete from "@/assets/img/trash-alt-regular.svg";
import Edit from "@/assets/img/edit-regular.svg";
import Private from "@/assets/img/user-secret-solid.svg";

import LazyImage from "@/components/Post/LazyImage";
import LazyVideo from "@/components/Post/LazyVideo";

import updatePost from "../utils/updatePost";

export default {
	components: {
		Save,
		Delete,
		Edit,
		Private,
		LazyImage,
		LazyVideo,
	},
	props: {
		post: Object,
		number: Number,
	},
	data() {
		return {
			postMessage: false,
			edit: false,
			newTitle: "",
			newTags: "",
		};
	},
	computed: {
		...mapState(["token"]),
		...mapGetters({
			admin: "isAdmin",
		}),
		editableTags() {
			return this.post.tags.join(" ");
		},
	},
	methods: {
		...mapActions(["resetState", "loading"]),
		...mapActions({
			loadPostsWithTags: "loadTaggedPosts",
			removePost: "deletePost",
		}),
		loadTaggedPosts(tag) {
			window.scrollTo(0, 0);

			const payload = {
				page: "tags",
				year: false,
			};

			this.resetState(payload);
			this.loadPostsWithTags(tag);
			this.loading(true);
		},
		deletePost() {
			let payload = {
				author: this.post.author,
				postId: this.post._id,
			};

			this.removePost(payload);
		},
		deletePostMessage() {
			this.postMessage = true;
		},
		hidePostMessage() {
			this.postMessage = false;
		},
		async saveUpdatedPost() {
			let newTitle = this.$refs.newTitle.value;
			let newTags = this.$refs.newTags.value;
			let oldTags = this.post.tags.join(" ");

			const payload = {
				_id: this.post._id,
			};

			if (newTitle != this.post.title) payload.title = newTitle;
			if (newTags != oldTags) payload.tags = newTags;
			payload.public = this.post.public;

			const response = await updatePost(this.$store.state.token, payload);

			this.post.title = response.title;
			this.post.tags = response.tags;
			this.post.public = response.public;
			this.edit = false;
		},
		async downloadMedia(path, index) {
			try {
				// Convert /images/ path to /api/images/ for the protected API route
				const apiPath = path.replace("/images/", "/api/images/");

				const response = await fetch(apiPath, {
					headers: {
						Authorization: this.token,
					},
				});

				if (!response.ok) {
					throw new Error(`Download failed: ${response.status}`);
				}

				const blob = await response.blob();
				const blobUrl = URL.createObjectURL(blob);

				// Determine file extension from path
				const ext = path.substring(path.lastIndexOf("."));
				const filename = `wojtagram-${this.post._id}-${index}${ext}`;

				// Create temporary link and trigger download
				const link = document.createElement("a");
				link.href = blobUrl;
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				// Clean up blob URL
				URL.revokeObjectURL(blobUrl);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error("Error downloading media:", error);
			}
		},
	},
};
</script>
